'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface SettingsSection {
  id: string;
  label: string;
  icon: string;
  description: string;
}

const SECTIONS: SettingsSection[] = [
  { id: 'profile', label: 'Profile & Account', icon: 'UserCircleIcon', description: 'Manage your personal details and password' },
  { id: 'organisation', label: 'Organisation', icon: 'BuildingOffice2Icon', description: 'Company name, logo, and tenant settings' },
  { id: 'users', label: 'Users & Roles', icon: 'UsersIcon', description: 'Manage team members and role permissions' },
  { id: 'sites', label: 'Sites & Locations', icon: 'MapPinIcon', description: 'Configure operational sites and warehouses' },
  { id: 'notifications', label: 'Notifications', icon: 'BellIcon', description: 'Alert preferences and email notification rules' },
  { id: 'integrations', label: 'Integrations', icon: 'PuzzlePieceIcon', description: 'Connect ERP, accounting, and third-party tools' },
  { id: 'audit', label: 'Audit Log', icon: 'ClockIcon', description: 'Full system activity and change history' },
  { id: 'billing', label: 'Billing & Plan', icon: 'CreditCardIcon', description: 'Subscription plan, invoices, and usage' },
];

const USERS = [
  { name: 'Arjun Mehta', email: 'arjun.mehta@lt-construction.com', role: 'Store Manager', status: 'Active', initials: 'AM', color: 'bg-secondary/20 text-secondary' },
  { name: 'Priya Sharma', email: 'priya.sharma@lt-construction.com', role: 'QHSE Manager', status: 'Active', initials: 'PS', color: 'bg-teal/20 text-teal' },
  { name: 'Rahul Kumar', email: 'rahul.kumar@lt-construction.com', role: 'Store Operator', status: 'Active', initials: 'RK', color: 'bg-amber-100 text-amber-700' },
  { name: 'Admin User', email: 'admin@lt-construction.com', role: 'Company Admin', status: 'Active', initials: 'AU', color: 'bg-purple-100 text-purple-700' },
  { name: 'Auditor', email: 'auditor@lt-construction.com', role: 'Auditor', status: 'Active', initials: 'AU', color: 'bg-slate-100 text-slate-600' },
];

const SITES = [
  { name: 'Site Alpha', code: 'SITE-A', type: 'Construction Site', manager: 'Arjun Mehta', items: 142 },
  { name: 'Site Beta', code: 'SITE-B', type: 'Construction Site', manager: 'Rahul Kumar', items: 98 },
  { name: 'Warehouse B', code: 'WH-B', type: 'Central Warehouse', manager: 'Arjun Mehta', items: 310 },
  { name: 'Site Gamma', code: 'SITE-G', type: 'Construction Site', manager: 'Priya Sharma', items: 67 },
];

const AUDIT_LOG = [
  { user: 'Arjun Mehta', action: 'Approved GRN-2026-0845', module: 'GRN', time: '29 Jul 2026, 14:22', type: 'approve' },
  { user: 'Rahul Kumar', action: 'Created GRN-2026-0847 (Draft)', module: 'GRN', time: '29 Jul 2026, 12:05', type: 'create' },
  { user: 'Priya Sharma', action: 'Logged QHSE Inspection — Site Beta', module: 'QHSE', time: '29 Jul 2026, 10:30', type: 'qhse' },
  { user: 'Arjun Mehta', action: 'Issued GI-2026-0317 to Kiran Bhat', module: 'GI', time: '29 Jul 2026, 09:15', type: 'issue' },
  { user: 'Admin User', action: 'Added new user: Meena Pillai', module: 'Settings', time: '28 Jul 2026, 17:40', type: 'settings' },
  { user: 'System', action: 'Low stock alert — Hydraulic Oil ISO 46', module: 'Inventory', time: '28 Jul 2026, 16:00', type: 'alert' },
  { user: 'Rahul Kumar', action: 'Updated item: Cement OPC 53 Grade', module: 'Item Master', time: '28 Jul 2026, 14:22', type: 'edit' },
  { user: 'Arjun Mehta', action: 'Rejected GI-2026-0310 — Insufficient stock', module: 'GI', time: '28 Jul 2026, 11:00', type: 'reject' },
];

const TYPE_COLOR: Record<string, string> = {
  approve: 'bg-teal/10 text-teal',
  create: 'bg-secondary/10 text-secondary',
  qhse: 'bg-purple-100 text-purple-600',
  issue: 'bg-orange-100 text-orange-600',
  alert: 'bg-amber-100 text-amber-600',
  settings: 'bg-slate-100 text-slate-600',
  edit: 'bg-blue-50 text-blue-600',
  reject: 'bg-red-50 text-red-600',
};

const TYPE_ICON: Record<string, string> = {
  approve: 'CheckCircleIcon',
  create: 'PlusCircleIcon',
  qhse: 'ShieldCheckIcon',
  issue: 'TruckIcon',
  alert: 'ExclamationTriangleIcon',
  settings: 'Cog6ToothIcon',
  edit: 'PencilSquareIcon',
  reject: 'XCircleIcon',
};

export default function SettingsPage() {
  const [activeSection, setActiveSection] = useState('profile');

  return (
    <AppLayout currentPath="/settings">
      <div className="flex gap-5 h-full">
        {/* Sidebar nav */}
        <div className="w-56 shrink-0 hidden md:block">
          <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
            <div className="px-4 py-3.5 border-b border-border">
              <p className="text-[11px] font-700 uppercase tracking-widest text-muted-foreground">Settings</p>
            </div>
            <nav className="p-2">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  className={[
                    'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-[12px] font-600 transition-all mb-0.5',
                    activeSection === s.id
                      ? 'bg-secondary text-white' :'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                  ].join(' ')}
                >
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={15} className="shrink-0" />
                  {s.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Mobile section tabs */}
        <div className="md:hidden w-full">
          <select
            value={activeSection}
            onChange={(e) => setActiveSection(e.target.value)}
            className="w-full px-3 py-2.5 bg-card border border-border rounded-xl text-[13px] text-foreground mb-4"
          >
            {SECTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Profile */}
          {activeSection === 'profile' && (
            <div className="bg-card border border-border rounded-2xl shadow-card p-6 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <Icon name="UserCircleIcon" size={18} className="text-secondary" />
                <h2 className="text-sm font-700 text-foreground">Profile & Account</h2>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-secondary/15 flex items-center justify-center text-secondary text-xl font-700">AM</div>
                <div>
                  <p className="font-700 text-foreground">Arjun Mehta</p>
                  <p className="text-[12px] text-muted-foreground">Store Manager · L&T Construction</p>
                  <button className="mt-1.5 text-[11px] text-secondary hover:underline font-600">Change avatar</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Full Name', value: 'Arjun Mehta' },
                  { label: 'Email Address', value: 'arjun.mehta@lt-construction.com' },
                  { label: 'Phone', value: '+91 98765 43210' },
                  { label: 'Role', value: 'Store Manager' },
                ].map((f) => (
                  <div key={f.label}>
                    <label className="text-[11px] font-600 text-muted-foreground uppercase tracking-wide block mb-1">{f.label}</label>
                    <input
                      defaultValue={f.value}
                      className="w-full px-3 py-2.5 bg-muted/30 border border-border rounded-xl text-[13px] text-foreground focus:outline-none focus:ring-2 focus:ring-secondary/30"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <button className="px-4 py-2 bg-secondary text-white rounded-xl text-[12px] font-600 hover:bg-secondary/90 transition-colors">Save Changes</button>
                <button className="px-4 py-2 bg-muted border border-border text-muted-foreground rounded-xl text-[12px] font-600 hover:text-foreground transition-colors">Change Password</button>
              </div>
            </div>
          )}

          {/* Organisation */}
          {activeSection === 'organisation' && (
            <div className="bg-card border border-border rounded-2xl shadow-card p-6 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <Icon name="BuildingOffice2Icon" size={18} className="text-secondary" />
                <h2 className="text-sm font-700 text-foreground">Organisation Settings</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Company Name', value: 'L&T Construction Pvt. Ltd.' },
                  { label: 'Tenant Code', value: 'LT-CONST-001' },
                  { label: 'Industry', value: 'Construction & Infrastructure' },
                  { label: 'Country', value: 'India' },
                  { label: 'Currency', value: 'INR (₹)' },
                  { label: 'Fiscal Year Start', value: 'April' },
                ].map((f) => (
                  <div key={f.label}>
                    <label className="text-[11px] font-600 text-muted-foreground uppercase tracking-wide block mb-1">{f.label}</label>
                    <input
                      defaultValue={f.value}
                      className="w-full px-3 py-2.5 bg-muted/30 border border-border rounded-xl text-[13px] text-foreground focus:outline-none focus:ring-2 focus:ring-secondary/30"
                    />
                  </div>
                ))}
              </div>
              <button className="px-4 py-2 bg-secondary text-white rounded-xl text-[12px] font-600 hover:bg-secondary/90 transition-colors">Save Changes</button>
            </div>
          )}

          {/* Users & Roles */}
          {activeSection === 'users' && (
            <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="UsersIcon" size={16} className="text-secondary" />
                  <h2 className="text-sm font-700 text-foreground">Users & Roles</h2>
                </div>
                <button className="flex items-center gap-1.5 px-3 py-2 bg-secondary text-white rounded-xl text-[12px] font-600 hover:bg-secondary/90 transition-colors">
                  <Icon name="PlusIcon" size={13} /> Invite User
                </button>
              </div>
              <div className="divide-y divide-border">
                {USERS.map((u) => (
                  <div key={u.email} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[12px] font-700 shrink-0 ${u.color}`}>{u.initials}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-600 text-foreground">{u.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <span className="text-[11px] font-600 text-muted-foreground hidden sm:block">{u.role}</span>
                    <span className="text-[10px] font-700 px-2 py-1 bg-teal/10 text-teal rounded-full">{u.status}</span>
                    <button className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors">
                      <Icon name="EllipsisVerticalIcon" size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sites */}
          {activeSection === 'sites' && (
            <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="MapPinIcon" size={16} className="text-secondary" />
                  <h2 className="text-sm font-700 text-foreground">Sites & Locations</h2>
                </div>
                <button className="flex items-center gap-1.5 px-3 py-2 bg-secondary text-white rounded-xl text-[12px] font-600 hover:bg-secondary/90 transition-colors">
                  <Icon name="PlusIcon" size={13} /> Add Site
                </button>
              </div>
              <div className="divide-y divide-border">
                {SITES.map((s) => (
                  <div key={s.code} className="flex items-center gap-3 px-5 py-4 hover:bg-muted/30 transition-colors">
                    <div className="w-9 h-9 rounded-xl bg-secondary/8 flex items-center justify-center shrink-0">
                      <Icon name="MapPinIcon" size={16} className="text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-600 text-foreground">{s.name}</p>
                        <span className="text-[10px] font-700 text-muted-foreground font-mono">{s.code}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{s.type} · Manager: {s.manager}</p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-[13px] font-700 text-foreground">{s.items}</p>
                      <p className="text-[10px] text-muted-foreground">items</p>
                    </div>
                    <button className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors">
                      <Icon name="PencilSquareIcon" size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeSection === 'notifications' && (
            <div className="bg-card border border-border rounded-2xl shadow-card p-6 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <Icon name="BellIcon" size={18} className="text-secondary" />
                <h2 className="text-sm font-700 text-foreground">Notification Preferences</h2>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Low Stock Alerts', desc: 'Notify when items fall below reorder level', enabled: true },
                  { label: 'GRN Pending Approval', desc: 'Alert when a GRN requires your approval', enabled: true },
                  { label: 'GI Pending Approval', desc: 'Alert when a goods issuance needs approval', enabled: true },
                  { label: 'QHSE Expiry Warnings', desc: 'Notify 30 days before item expiry', enabled: true },
                  { label: 'BOM Over-Consumption', desc: 'Alert when BOM line exceeds planned quantity', enabled: false },
                  { label: 'Daily Summary Email', desc: 'Receive a daily digest of all activity', enabled: false },
                  { label: 'System Maintenance', desc: 'Notify about scheduled downtime', enabled: true },
                ].map((n) => (
                  <div key={n.label} className="flex items-center justify-between p-4 bg-muted/20 border border-border rounded-xl">
                    <div>
                      <p className="text-[13px] font-600 text-foreground">{n.label}</p>
                      <p className="text-[11px] text-muted-foreground">{n.desc}</p>
                    </div>
                    <div className={`w-10 h-5 rounded-full transition-colors cursor-pointer ${n.enabled ? 'bg-secondary' : 'bg-muted-foreground/30'} relative`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${n.enabled ? 'left-5' : 'left-0.5'}`} />
                    </div>
                  </div>
                ))}
              </div>
              <button className="px-4 py-2 bg-secondary text-white rounded-xl text-[12px] font-600 hover:bg-secondary/90 transition-colors">Save Preferences</button>
            </div>
          )}

          {/* Integrations */}
          {activeSection === 'integrations' && (
            <div className="bg-card border border-border rounded-2xl shadow-card p-6 space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <Icon name="PuzzlePieceIcon" size={18} className="text-secondary" />
                <h2 className="text-sm font-700 text-foreground">Integrations</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { name: 'SAP ERP', desc: 'Sync purchase orders and GRNs', status: 'Connected', icon: 'ServerIcon', color: 'text-teal' },
                  { name: 'Tally Prime', desc: 'Accounting and invoice sync', status: 'Not Connected', icon: 'CalculatorIcon', color: 'text-muted-foreground' },
                  { name: 'Email (SMTP)', desc: 'Notification and alert emails', status: 'Connected', icon: 'EnvelopeIcon', color: 'text-teal' },
                  { name: 'WhatsApp Business', desc: 'Mobile alerts for approvals', status: 'Not Connected', icon: 'ChatBubbleLeftRightIcon', color: 'text-muted-foreground' },
                ].map((intg) => (
                  <div key={intg.name} className="flex items-center gap-3 p-4 border border-border rounded-xl hover:border-secondary/30 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center shrink-0">
                      <Icon name={intg.icon as Parameters<typeof Icon>[0]['name']} size={18} className={intg.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-600 text-foreground">{intg.name}</p>
                      <p className="text-[11px] text-muted-foreground">{intg.desc}</p>
                    </div>
                    <span className={`text-[10px] font-700 px-2 py-1 rounded-full ${intg.status === 'Connected' ? 'bg-teal/10 text-teal' : 'bg-muted text-muted-foreground'}`}>
                      {intg.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit Log */}
          {activeSection === 'audit' && (
            <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="ClockIcon" size={16} className="text-secondary" />
                  <h2 className="text-sm font-700 text-foreground">Audit Log</h2>
                </div>
                <button className="flex items-center gap-1.5 px-3 py-2 bg-muted border border-border text-muted-foreground rounded-xl text-[12px] font-600 hover:text-foreground transition-colors">
                  <Icon name="ArrowDownTrayIcon" size={13} /> Export
                </button>
              </div>
              <div className="divide-y divide-border">
                {AUDIT_LOG.map((a, i) => (
                  <div key={`log-${i}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${TYPE_COLOR[a.type]}`}>
                      <Icon name={TYPE_ICON[a.type] as Parameters<typeof Icon>[0]['name']} size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[12px] font-600 text-foreground">{a.user}</p>
                        <span className="text-[10px] font-600 px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{a.module}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">{a.action}</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground/60 shrink-0 whitespace-nowrap hidden sm:block">{a.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Billing */}
          {activeSection === 'billing' && (
            <div className="bg-card border border-border rounded-2xl shadow-card p-6 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <Icon name="CreditCardIcon" size={18} className="text-secondary" />
                <h2 className="text-sm font-700 text-foreground">Billing & Plan</h2>
              </div>
              <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-2xl flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-secondary/15 flex items-center justify-center">
                  <Icon name="StarIcon" size={22} className="text-secondary" />
                </div>
                <div className="flex-1">
                  <p className="font-700 text-foreground">Professional Plan</p>
                  <p className="text-[12px] text-muted-foreground">Up to 50 users · 4 sites · All modules</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-700 text-secondary">₹12,000</p>
                  <p className="text-[11px] text-muted-foreground">per month</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Active Users', value: '5 / 50' },
                  { label: 'Sites', value: '4 / 4' },
                  { label: 'Next Renewal', value: '01 Aug 2026' },
                ].map((s) => (
                  <div key={s.label} className="p-3 bg-muted/30 border border-border rounded-xl text-center">
                    <p className="text-[10px] font-600 text-muted-foreground uppercase tracking-wide">{s.label}</p>
                    <p className="text-[15px] font-700 text-foreground mt-1">{s.value}</p>
                  </div>
                ))}
              </div>
              <button className="px-4 py-2 bg-secondary text-white rounded-xl text-[12px] font-600 hover:bg-secondary/90 transition-colors">Manage Subscription</button>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
